import gsap from 'gsap';
import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';
import { createApp, onMounted, onUnmounted, ref } from 'vue';

import { ANIMATION_CONFIGS } from '~/constants/animations';
import * as DataDogLogs from '~/lib/datadog/datadog';

import { BaseSection } from './base-section';

/**
 * @type {import('swiper/types').SwiperOptions}
 */
const SWIPER_SETTINGS = {
  breakpoints: {
    0: {
      centeredSlides: true,
      centeredSlidesBounds: true,
      slidesPerView: 1.4,
    },
    768: {
      centeredSlides: false,
      slidesPerView: 3.5,
    },
  },
  modules: [Navigation],
  navigation: {
    nextEl: '.swiper-next-button',
    prevEl: '.swiper-prev-button',
  },
  slidesOffsetAfter: 10,
  slidesOffsetBefore: 10,
  spaceBetween: 10,
  updateOnWindowResize: true,
};

/**
 * Featured Story Carousel
 *
 * This section displays carousel of images and captions
 *
 * @extends BaseSection
 * @constructor
 * @param {HTMLElement} container - The container element for the section
 */
class FeaturedStoryCarousel extends BaseSection {
  constructor() {
    super('[data-section-type="featured-story-carousel"]');
  }

  init(instanceData) {
    const container = instanceData.container;

    if (!container) {
      DataDogLogs.error(
        '[Allbirds section] - Featured Story Carousel - init - Container not found',
        {},
        new Error('Container not found'),
      );
      return;
    }

    const app = createApp({
      name: 'FeaturedStoryCarouselSection',
      setup() {
        const carousel = ref(null);

        onMounted(() => {
          const swiperContainer = container.querySelector('.swiper');
          if (swiperContainer) {
            carousel.value = new Swiper(swiperContainer, SWIPER_SETTINGS);
          }
        });

        onUnmounted(() => {
          carousel.value?.destroy();
          carousel.value = null;
        });

        return {};
      },
    });
    app.mount(container);
  }

  /**
   * Sets up the animation timeline for the section that will play when it comes into view
   * This will be called automatically during initialization
   *
   * @param {Object} instanceData - The section instance data
   * @return {Object} The GSAP timeline for the animation
   */
  setupInViewAnimationTimeline(instanceData) {
    const container = instanceData.container;

    if (!container) {
      DataDogLogs.error(
        '[Allbirds section] - Featured Story Carousel - setupInViewAnimationTimeline - Missing elements for in-view animation',
        {},
        new Error('Missing elements for in-view animation'),
      );
      return null;
    }

    const tl = gsap.timeline({
      paused: true,
      scrollTrigger: {
        ...this.getInViewAnimationOptions(),
        trigger: container,
      },
    });

    if (!tl) {
      return null;
    }

    tl.to(container, ANIMATION_CONFIGS.FADE_IN, 0);

    return tl;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new FeaturedStoryCarousel();
});
